import { Button } from '@auctisync/ui';
import { useState, type CSSProperties } from 'react';
import { useToast } from './Toast';
import { invokeEF } from '../lib/edgeFunctions';

/**
 * Bidder-facing "I'll pay in person" declaration.
 *
 * Shown on the bidder invoice page ONLY when the auction house (tenant) has
 * enabled cash and/or check in tenants.metadata.invoice_settings.accepted_methods.
 * Renders one button per enabled offline method. Clicking it calls the
 * declare-offline-payment Edge Function, which records the intent as a
 * 'declared_offline' invoice_actions audit row.
 *
 * This is a declaration of INTENT — it does NOT pay the invoice or change its
 * status. The invoice stays "Due"; staff confirm receipt at pickup. After a
 * successful declaration we swap to a confirmation note and call onDeclared so
 * the parent can refresh.
 */

type OfflineMethod = 'cash' | 'check';

const METHOD_LABEL: Record<OfflineMethod, string> = {
  cash: 'cash',
  check: 'a check',
};

export function BidderOfflineDeclare({
  invoiceId,
  tenantId,
  acceptedMethods,
  onDeclared,
}: {
  invoiceId: string;
  tenantId: string;
  acceptedMethods: { cash: boolean; check: boolean };
  onDeclared?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<OfflineMethod | null>(null);
  const [declared, setDeclared] = useState(false);

  const enabled = (['cash', 'check'] as const).filter((m) => acceptedMethods[m]);

  // Nothing to offer — the tenant hasn't enabled any offline method.
  if (enabled.length === 0) return null;

  async function declare(method: OfflineMethod) {
    setBusy(method);
    const { error } = await invokeEF<{ ok: boolean }>('declare-offline-payment', {
      invoice_id: invoiceId,
      // tenant_id is not required by the EF (it resolves the tenant from the
      // invoice), but pass it so the request is self-describing for logs.
      tenant_id: tenantId,
      method,
    });
    setBusy(null);
    if (error) {
      toast.error(
        error === 'method_not_accepted'
          ? 'That payment option is no longer available from this auction house.'
          : 'Could not record your choice. Please try again.',
      );
      return;
    }
    setDeclared(true);
    toast.success('Thanks — we let the auction house know.');
    onDeclared?.();
  }

  if (declared) {
    return (
      <section style={cardStyle}>
        <h2 style={headingStyle}>Paying in person</h2>
        <p style={noteStyle}>Marked to pay in person — the auction house will confirm at pickup.</p>
      </section>
    );
  }

  return (
    <section style={cardStyle}>
      <h2 style={headingStyle}>Prefer to pay in person?</h2>
      <p style={{ ...noteStyle, marginBottom: '0.9rem' }}>
        Let the auction house know you&apos;ll settle up at pickup. They&apos;ll confirm your
        payment when you collect your lots.
      </p>
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        {enabled.map((m) => (
          <Button
            key={m}
            variant="outline"
            disabled={busy !== null}
            onClick={() => {
              void declare(m);
            }}
          >
            {busy === m ? 'Saving…' : `I'll pay ${METHOD_LABEL[m]} at pickup`}
          </Button>
        ))}
      </div>
    </section>
  );
}

const cardStyle: CSSProperties = {
  marginTop: '1.5rem',
  padding: '1.1rem 1.25rem',
  borderRadius: '16px',
  background: 'var(--surface-elev)',
  border: '1px solid var(--brand-hairline, var(--border))',
  boxShadow: 'var(--elev-2)',
};

const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: '0.78rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-muted)',
  marginTop: 0,
  marginBottom: '0.85rem',
};

const noteStyle: CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
  margin: 0,
};
